<template>
	<v-container>
		<v-row class="contentRow" align="center">
			<v-col>
				<h1>Self Assessment Score of Your Connections</h1>
			</v-col>
		</v-row>
		<p style="margin: 30px 20px;">
			NIS stands for Network and Information Systems.
			Explore your connections' NIS Cyber Security scores in detail under this tab.
			This score acts as a standard for measuring progress and ensuring adherence to
			responsible security practices, aligning with the EU NIS2 Directive
			(the Network and Information Systems Directive). Here your
			connections' score awaits your attention to have insights into the cybersecurity
			maturity of your supply chain.
		</p>
		<div style="display: flex;">
			<v-col align="end">
				<h3 style="margin-right: 60px;">Sort by : </h3>
			</v-col>
			<v-col>
				<v-select
					v-model="selectedSort"
					:items="dropdownItems"
					item-text="title"
					item-value="title"
					placeholder="Select sorting criteria"
					style="width: 220px; margin-top: -5px; margin-left: -60px;"
					dense
					outlined
					@change="handleSortChange"
				/>
			</v-col>
			<v-col align="end">
				<h3 style="margin-right: 60px;">Sort by Project : </h3>
			</v-col>
			<v-col>
				<v-select
					v-model="selectedProjectSort"
					:items="projects"
					item-text="name"
					item-value="id"
					placeholder="Select project"
					style="width: 200px; margin-top: -5px; margin-left: -60px;"
					dense
					outlined
					@change="handleProjectSortChange"
				/>
			</v-col>
		</div>

		<v-card>
			<v-card-title>
				<v-text-field
					v-model="search"
					append-icon="mdi-magnify"
					label="Search"
					single-line
					hide-details
				/>
			</v-card-title>
			<v-data-table
				theme--light
				:headers="headers"
				:items="connections"
				:search="search"
				:items-per-page="10"
				class="light table"
				:footer-props="{ 'items-per-page-options': [10, 15, 20] }"
			>
				<template v-slot:item.averageScore="{ item }">
					<v-progress-circular
						:rotate="360"
						:size="40"
						:width="5"
						:value="item.averageScore"
						:color="getColorOnScore(item.averageScore)"
					>
						{{ item.averageScore }}
					</v-progress-circular>
				</template>
				<template v-slot:item.importanceofsupplier="{ item }">
					<v-select
						class="smalldropdown"
						style="max-width: 150px;"
						:items="importanceOptions"
						v-model="item.importanceofsupplier"
						@change="logImportanceChange(item)"
					>
						<template v-slot:item="{ item: opt }">
							<v-list-item-content>
								<v-list-item-title>
									<v-icon
										color="primary"
										size="10"
										class="mr-2"
										v-for="i in opt.icon"
										:key="i"
									>fas fa-circle</v-icon>
								</v-list-item-title>
							</v-list-item-content>
						</template>
						<template v-slot:selection="{ item: opt }">
							<v-icon
								color="primary"
								size="10"
								class="mr-1 ml-2"
								v-for="i in opt.icon"
								:key="i"
							>fas fa-circle</v-icon>
						</template>
					</v-select>
				</template>
				<template v-slot:item.suppliersfilledout="{ item }">
					<span style="padding-left: 30px;">
						<v-tooltip top>
							<template v-slot:activator="{ on, attrs }">
								<span v-bind="attrs" v-on="on">
									{{ item.noOfSuppliersFilled || 0 }}/{{ item.noConnectedsuplliers || 0 }}
								</span>
							</template>
							<span>No of connections filled out/Total no of connections</span>
						</v-tooltip>
					</span>
				</template>
				<template v-slot:item.countryofoperation="{ item }">
					<span style="padding-left: 20px;">{{ item.countryofoperation }}</span>
				</template>
				<template v-slot:item.SupplierScore="{ item }">
					<v-progress-circular
						:rotate="360"
						:size="40"
						:width="5"
						:value="item.supplierScore"
						:color="getColorOnScore(item.supplierScore)"
						@click="openDialog(item)"
						style="cursor: pointer;"
					>
						{{ item.supplierScore }}
					</v-progress-circular>
				</template>
				<template v-slot:item.filledoutdate="{ item }">
					<span
						v-if="isDateMoreThanOneYearOld(item.filledoutdate)"
						:style="getFilledOutDateStyle(item.filledoutdate)"
					>
						<v-tooltip top>
							<template v-slot:activator="{ on, attrs }">
								<span v-bind="attrs" v-on="on">{{ item.filledoutdate }}</span>
							</template>
							<span>Last filled out date is more than 1 year</span>
						</v-tooltip>
					</span>
					<span v-else :style="getFilledOutDateStyle(item.filledoutdate)">
						{{ item.filledoutdate }}
					</span>
				</template>
			</v-data-table>
		</v-card>

		<v-dialog v-model="dialog" max-width="720px">
			<v-card class="pa-4">
				<div class="d-flex justify-end">
					<v-btn icon @click="dialog = false">
						<v-icon color="primary">fas fa-times</v-icon>
					</v-btn>
				</div>
				<nis-score-rings
					v-if="dialogItem"
					:score1="dialogItem.score1"
					:score2="dialogItem.score2"
					:score3="dialogItem.score3"
					:score4="dialogItem.score4"
					:score5="dialogItem.score5"
					:score6="dialogItem.score6"
					:title="`${dialogItem.name || 'Supplier'} NIS Score`"
				/>
				<p class="text-center mt-4" style="font-weight: bold;">
					Filled out : {{ dialogItem.filledoutdate }}
				</p>
				<div class="text-center mt-4" v-if="dialogItem.filledoutdate === 'N/A' || isDateMoreThanOneYearOld(dialogItem.filledoutdate)">
					<v-btn color="orange" dark @click="notify(dialogItem.clientID)">
						Request to fill out NIS Survey
					</v-btn>
				</div>
			</v-card>
		</v-dialog>

		<v-dialog v-model="dialogComplete" max-width="550px">
			<v-card class="d-flex flex-column align-center justify-center">
				<v-card-title class="text-center">
					Success! Request Sent to your Supplier
				</v-card-title>
				<v-card-actions class="justify-center">
					<v-btn color="primary" text @click="dialogComplete = false">OK</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import NisApi from "../api/nis/nis"
import { SupplierImpactAPI } from "../api/suppliers/supplierimpact"
import LoadingState from "../consts/LoadingState"
import ConnectionAPI from "../api/connections/connection"
import NisScoreRings from "../components/NisScoreRings.vue"

export default {
	name: "NisConnectionsTable",
	components: {
		"nis-score-rings": NisScoreRings,
	},
	data() {
		return {
			importanceOptions: [
				{ text: "HIGH", icon: 3 },
				{ text: "MEDIUM", icon: 2 },
				{ text: "LOW", icon: 1 },
			],
			selectedSort: "Total score",
			connections: [],
			allConnections: [],
			importanceClientArray: [],
			dropdownItems: [
				{ title: "Total score", action: () => this.sortByTotalScore() },
				{ title: "Governance", action: () => this.sortByScore(1) },
				{ title: "Incidents", action: () => this.sortByScore(2) },
				{ title: "Continuity", action: () => this.sortByScore(3) },
				{ title: "Supply Chain", action: () => this.sortByScore(4) },
				{ title: "Technical", action: () => this.sortByScore(5) },
				{ title: "People", action: () => this.sortByScore(6) },
				{ title: "Impact Level", action: () => this.sortByImportance() },
			],
			search: "",
			dialog: false,
			dialogComplete: false,
			dialogItem: {},
			projects: [],
			selectedProjectSort: null,
			headers: [
				{ text: "Supplier", align: "start", value: "name", sortable: false },
				{ text: "Country of Operation", value: "countryofoperation", sortable: false },
				{ text: "Company Score", value: "SupplierScore", sortable: false },
				{ text: "Suppliers filled out", value: "suppliersfilledout", sortable: false },
				{ text: "Supply Chain Score", value: "averageScore", sortable: false },
				{ text: "Impact of supplier", value: "importanceofsupplier", sortable: false },
				{ text: "Filled Out Date", value: "filledoutdate", sortable: false },
			],
			scoreColumnLabels: {
				"Total score": "Company Score",
				Governance: "Governance Score",
				Incidents: "Incidents Score",
				Continuity: "Continuity Score",
				"Supply Chain": "Supply Chain (Part) Score",
				Technical: "Technical Score",
				People: "People Score",
				"Impact Level": "Company Score",
			},
		}
	},
	async mounted() {
		await this.loadItems()
		await this.loadProjects()
		this.allConnections = [...this.connections]
	},
	methods: {
		avgSix(scores) {
			const vals = scores.filter((s) => s != null && !isNaN(s))
			if (!vals.length) return 0
			return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
		},
		partScore(item, part) {
			const value = item[`score${part}`]
			return value == null || isNaN(value) ? 0 : Number(value)
		},
		totalCompanyScore(item) {
			return this.avgSix([
				item.score1,
				item.score2,
				item.score3,
				item.score4,
				item.score5,
				item.score6,
			])
		},
		supplyChainScoreForPart(item, part) {
			const scores = item.averageConnectionScores || []
			const value = scores[part - 1]
			return value == null || isNaN(value) ? 0 : Math.round(Number(value))
		},
		totalSupplyChainScore(item) {
			const scores = item.averageConnectionScores || []
			if (scores[0] == null) return 0
			return this.avgSix(scores.map((s) => (s == null ? null : Math.round(s))))
		},
		updateScoreHeader(title) {
			const label = this.scoreColumnLabels[title] || "Company Score"
			this.headers = this.headers.map((header) =>
				header.value === "SupplierScore" ? { ...header, text: label } : header
			)
		},
		applyDisplayedScores(mode) {
			this.connections = this.connections.map((item) => {
				let supplierScore = this.totalCompanyScore(item)
				let averageScore = this.totalSupplyChainScore(item)
				if (typeof mode === "number") {
					supplierScore = this.partScore(item, mode)
					averageScore = this.supplyChainScoreForPart(item, mode)
				}
				return {
					...item,
					supplierScore,
					averageScore,
				}
			})
		},
		async loadItems() {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
			try {
				const data = await NisApi.getNisSuppliers()
				this.connections = []
				this.importanceClientArray = []
				for (let entry of data) {
					const importance = this.getImportance(entry.importance)
					let importanceScore = entry.importance != null ? entry.importance : 0
					let scores = [0, 0, 0, 0, 0, 0]
					let nisid = 0
					let filledoutdate = "N/A"
					if (entry.nis) {
						scores = [
							entry.nis.score1 == null ? 0 : Math.round(entry.nis.score1),
							entry.nis.score2 == null ? 0 : Math.round(entry.nis.score2),
							entry.nis.score3 == null ? 0 : Math.round(entry.nis.score3),
							entry.nis.score4 == null ? 0 : Math.round(entry.nis.score4),
							entry.nis.score5 == null ? 0 : Math.round(entry.nis.score5),
							entry.nis.score6 == null ? 0 : Math.round(entry.nis.score6),
						]
						nisid = entry.nis.id
						if (entry.nis.timestamp) {
							const date = new Date(entry.nis.timestamp)
							const month = date.toLocaleString("default", { month: "short" })
							filledoutdate = `${month} ${date.getFullYear()}`
						}
					}
					const row = {
						clientID: entry.clientID,
						name: entry.name,
						nis: entry.nis,
						averageConnectionScores: entry.averageSupplierScores || [],
						noOfSuppliersFilled: entry.noOfSuppliersFilled,
						totalsuppliers: entry.totalsuppliers,
						noConnectedsuplliers: entry.noConnectedsuplliers,
						countryofoperation: entry.COO,
						importanceofsupplier: importance,
						importanceScore,
						score1: scores[0],
						score2: scores[1],
						score3: scores[2],
						score4: scores[3],
						score5: scores[4],
						score6: scores[5],
						filledoutdate,
						nisid,
						supplierScore: 0,
						averageScore: 0,
					}
					row.supplierScore = this.totalCompanyScore(row)
					row.averageScore = this.totalSupplyChainScore(row)
					this.connections.push(row)
					this.importanceClientArray.push({
						importance: entry.importance,
						clientID: entry.clientID,
					})
				}
				this.allConnections = [...this.connections]
				this.handleSortChange(this.selectedSort)
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_END_LOADING,
					null,
					{ root: true }
				)
			} catch (error) {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					error,
					{ root: true }
				)
			}
		},
		loadProjects() {
			ConnectionAPI.getProjects()
				.then((data) => {
					this.projects = data.map((project) => ({
						id: project.ProjectID,
						name: project.ProjectName,
						clientIDs:
							typeof project.Involved === "string"
								? project.Involved.split(",").map((id) => parseInt(id, 10))
								: project.Involved,
					}))
					this.projects = [...this.projects, { id: null, name: "All Projects" }]
				})
				.catch(() => {})
		},
		openDialog(item) {
			this.dialogItem = item
			this.dialog = true
		},
		async notify(clientID) {
			try {
				await NisApi.sendInvitation([clientID])
				this.dialogComplete = true
			} catch (reason) {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					reason,
					{ root: true }
				)
			}
		},
		getImportance(importance) {
			switch (importance) {
			case 1:
				return { text: "LOW", icon: 1 }
			case 2:
				return { text: "MEDIUM", icon: 2 }
			case 3:
				return { text: "HIGH", icon: 3 }
			default:
				return {}
			}
		},
		getFilledOutDateStyle(filledoutdate) {
			return this.isDateMoreThanOneYearOld(filledoutdate)
				? { color: "red", fontWeight: "bold", paddingLeft: "20px" }
				: { paddingLeft: "20px" }
		},
		isDateMoreThanOneYearOld(filledoutdate) {
			if (!filledoutdate || filledoutdate === "N/A") return false
			const dateParts = filledoutdate.split(" ")
			const date = new Date(`${dateParts[0]} 1, ${dateParts[1]}`)
			const oneYearAgo = new Date()
			oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
			return date < oneYearAgo
		},
		getColorOnScore(score) {
			if (score >= 70) return "success"
			if (score >= 40) return "warning"
			return "error"
		},
		handleProjectSortChange() {
			if (this.selectedProjectSort) {
				const selectedProject = this.projects.find(
					(p) => p.id === this.selectedProjectSort
				)
				if (selectedProject && selectedProject.clientIDs) {
					this.connections = this.allConnections.filter((c) =>
						selectedProject.clientIDs.includes(c.clientID)
					)
				} else {
					this.connections = [...this.allConnections]
				}
			} else {
				this.connections = [...this.allConnections]
			}
			this.handleSortChange(this.selectedSort)
		},
		handleSortChange(selectedTitle) {
			const title = selectedTitle || this.selectedSort
			const selectedItem = this.dropdownItems.find((item) => item.title === title)
			this.updateScoreHeader(title)
			if (selectedItem) selectedItem.action()
		},
		sortByTotalScore() {
			this.applyDisplayedScores("total")
			this.connections = [...this.connections].sort(
				(b, a) => a.supplierScore - b.supplierScore
			)
		},
		sortByScore(n) {
			this.applyDisplayedScores(n)
			this.connections = [...this.connections].sort(
				(b, a) => a.supplierScore - b.supplierScore
			)
		},
		sortByImportance() {
			this.applyDisplayedScores("total")
			this.connections = [...this.connections].sort(
				(b, a) => a.importanceScore - b.importanceScore
			)
		},
		async logImportanceChange(item) {
			let impact = 2
			const val = item.importanceofsupplier
			const text = typeof val === "object" ? val.text : val
			const icon = typeof val === "object" ? val.icon : null
			if (text === "HIGH" || icon === 3) impact = 3
			else if (text === "LOW" || icon === 1) impact = 1

			for (const obj of this.importanceClientArray) {
				if (obj.clientID == item.clientID) {
					try {
						if (obj.importance === null || obj.importance === undefined) {
							await SupplierImpactAPI.createSupplierimpact(
								item.clientID,
								impact,
								new Date().getFullYear()
							)
						} else {
							await SupplierImpactAPI.updateSupplierimpactById(
								item.clientID,
								impact,
								new Date().getFullYear()
							)
						}
						await this.loadItems()
					} catch (error) {
						this.$store.dispatch(
							"loading/" + LoadingState.ACTION_SET_ERROR,
							error,
							{ root: true }
						)
					}
				}
			}
		},
	},
}
</script>
